import React, { useState, useEffect, useCallback, useMemo } from "react";
import { toast } from "react-hot-toast";
import adminService from "../../services/adminService";
import CircuitReservationAdminModal from "./CircuitReservationAdminModal";

/* ---------- Helpers ---------- */
const money = (n) =>
  n === null || n === undefined || n === ""
    ? "—"
    : new Intl.NumberFormat("fr-MA", { style: "currency", currency: "MAD", minimumFractionDigits: 2 }).format(Number(n));

const formatDate = (d) =>
  d ? new Date(d).toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" }) : "—";

const STATUT_META = {
  "en attente d'affectation": { label: "À affecter", cls: "bg-amber-100 text-amber-700" },
  affecté: { label: "Affecté", cls: "bg-blue-100 text-blue-700" },
  assigné: { label: "Affecté", cls: "bg-blue-100 text-blue-700" },
  devis_envoye: { label: "Devis envoyé", cls: "bg-indigo-100 text-indigo-700" },
  confirmé: { label: "Confirmé", cls: "bg-teal-100 text-teal-700" },
  payé: { label: "Payé", cls: "bg-emerald-100 text-emerald-700" },
  annulé: { label: "Annulé", cls: "bg-red-100 text-red-700" },
};

const StatutBadge = ({ statut }) => {
  const m = STATUT_META[statut] || { label: statut || "—", cls: "bg-slate-100 text-slate-600" };
  return <span className={`px-2.5 py-1 inline-flex text-[11px] leading-5 font-bold rounded-full ${m.cls}`}>{m.label}</span>;
};

const UNASSIGNED = "__unassigned__";

/* ---------- Composant ---------- */
const CircuitReservationsManager = () => {
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("tous");
  const [selected, setSelected] = useState(null);
  const [collapsed, setCollapsed] = useState({}); // { [groupKey]: true }

  const fetchReservations = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminService.getAllItems("reservationCircuits", { limit: 300 });
      const items = res?.data?.items || res?.data || [];
      setReservations(Array.isArray(items) ? items : []);
    } catch (e) {
      toast.error(e.message || "Erreur de chargement des réservations");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchReservations();
  }, [fetchReservations]);

  // Filtrage
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return reservations.filter((r) => {
      if (statusFilter !== "tous" && r.statut !== statusFilter) return false;
      if (!q) return true;
      return [r.fullName, r.email, r.telephone, r.reference, r.circuit?.titre]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q));
    });
  }, [reservations, search, statusFilter]);

  // Groupement par circuit
  const groups = useMemo(() => {
    const map = new Map();
    filtered.forEach((r) => {
      const key = r.circuit?._id || UNASSIGNED;
      if (!map.has(key)) {
        map.set(key, {
          key,
          circuit: r.circuit || null,
          title: r.circuit?.titre || "Non affecté à un circuit",
          image: r.circuit?.image || null,
          items: [],
          revenue: 0,
          paidCount: 0,
        });
      }
      const g = map.get(key);
      g.items.push(r);
      if (r.statut === "payé") {
        g.revenue += Number(r.prixTotal || r.paiement?.montantPaye || 0);
        g.paidCount += 1;
      }
    });
    // Non affecté en dernier, sinon par nb de résas desc
    return Array.from(map.values()).sort((a, b) => {
      if (a.key === UNASSIGNED) return 1;
      if (b.key === UNASSIGNED) return -1;
      return b.items.length - a.items.length;
    });
  }, [filtered]);

  const totalRevenue = useMemo(() => groups.reduce((s, g) => s + g.revenue, 0), [groups]);

  const toggle = (key) => setCollapsed((c) => ({ ...c, [key]: !c[key] }));

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="bg-white rounded-3xl shadow-sm border border-slate-100 p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-2xl font-black text-slate-800 flex items-center gap-2">📝 Réservations Circuits</h2>
            <p className="text-sm text-slate-500">Regroupées par circuit · paiement & suivi</p>
          </div>
          <div className="flex gap-3">
            <div className="rounded-2xl bg-gradient-to-br from-slate-700 to-slate-900 text-white px-4 py-2.5">
              <p className="text-[10px] uppercase tracking-wide text-white/60 font-bold">Réservations</p>
              <p className="text-lg font-black">{reservations.length}</p>
            </div>
            <div className="rounded-2xl bg-gradient-to-br from-emerald-500 to-emerald-700 text-white px-4 py-2.5">
              <p className="text-[10px] uppercase tracking-wide text-white/70 font-bold">Encaissé (payé)</p>
              <p className="text-lg font-black">{money(totalRevenue)}</p>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap gap-3 mt-5">
          <input
            type="text"
            placeholder="🔎 Rechercher (client, email, réf, circuit)…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="flex-1 min-w-[220px] px-4 py-2.5 border-2 border-slate-200 rounded-xl focus:border-amber-400 outline-none text-sm"
          />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2.5 border-2 border-slate-200 rounded-xl text-sm bg-white"
          >
            <option value="tous">Tous statuts</option>
            <option value="en attente d'affectation">À affecter</option>
            <option value="affecté">Affecté</option>
            <option value="devis_envoye">Devis envoyé</option>
            <option value="confirmé">Confirmé</option>
            <option value="payé">Payé</option>
            <option value="annulé">Annulé</option>
          </select>
        </div>
      </div>

      {/* Contenu */}
      {loading ? (
        <div className="space-y-4">
          <style>{`.nz-skel{position:relative;overflow:hidden;background:#eef2f7}.nz-skel::after{content:"";position:absolute;inset:0;transform:translateX(-100%);background:linear-gradient(90deg,transparent,rgba(255,255,255,.7),transparent);animation:nz-shimmer 1.4s infinite}@keyframes nz-shimmer{100%{transform:translateX(100%)}}`}</style>
          {[...Array(3)].map((_, i) => (
            <div key={i} className="bg-white rounded-3xl border border-slate-100 p-5 space-y-3">
              <div className="h-5 w-1/3 rounded nz-skel" />
              <div className="h-16 w-full rounded-2xl nz-skel" />
              <div className="h-16 w-full rounded-2xl nz-skel" />
            </div>
          ))}
        </div>
      ) : groups.length === 0 ? (
        <div className="bg-white rounded-3xl border border-slate-100 text-center py-16">
          <div className="text-5xl mb-3">📭</div>
          <p className="text-slate-500">Aucune réservation de circuit.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {groups.map((g) => {
            const isCollapsed = collapsed[g.key];
            return (
              <div key={g.key} className="bg-white rounded-3xl shadow-sm border border-slate-100 overflow-hidden">
                {/* En-tête groupe */}
                <button
                  onClick={() => toggle(g.key)}
                  className="w-full flex items-center gap-4 px-5 py-4 hover:bg-slate-50 transition-colors text-left"
                >
                  <div className="w-14 h-14 flex-none rounded-2xl overflow-hidden bg-slate-100 flex items-center justify-center">
                    {g.image ? (
                      <img src={g.image} alt="" className="w-full h-full object-cover" onError={(e) => (e.currentTarget.style.display = "none")} />
                    ) : (
                      <span className="text-2xl">{g.key === UNASSIGNED ? "❓" : "🗺️"}</span>
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-black text-slate-800 truncate">{g.title}</p>
                    <p className="text-xs text-slate-400">
                      {g.items.length} réservation(s) · {g.paidCount} payée(s)
                      {g.circuit?.prix ? ` · ${money(g.circuit.prix)}/pers` : ""}
                    </p>
                  </div>
                  {g.revenue > 0 && (
                    <span className="hidden sm:inline-flex text-sm font-bold text-emerald-700 bg-emerald-50 px-3 py-1.5 rounded-xl">
                      {money(g.revenue)}
                    </span>
                  )}
                  <span className={`text-slate-400 transition-transform ${isCollapsed ? "" : "rotate-180"}`}>▾</span>
                </button>

                {/* Liste des réservations du groupe */}
                {!isCollapsed && (
                  <div className="px-4 lg:px-5 pb-5 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                    {g.items.map((r) => (
                      <div
                        key={r._id}
                        className="rounded-2xl border border-slate-100 p-4 hover:shadow-md transition-all flex flex-col bg-slate-50/40"
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <p className="font-bold text-slate-800 truncate">{r.fullName || "Client"}</p>
                            <p className="text-[11px] font-mono text-slate-400">{r.reference || r._id?.slice(-8)}</p>
                          </div>
                          <StatutBadge statut={r.statut} />
                        </div>

                        <div className="mt-2.5 space-y-1 text-xs text-slate-600 flex-1">
                          <p className="truncate">✉️ {r.email || "—"}</p>
                          <p>📞 {r.telephone || "—"}</p>
                          <p>📅 {formatDate(r.dateDebut)} · 👥 {r.nombrePersonnes || "?"}</p>
                          <p className="font-bold text-slate-800 text-sm pt-1">{money(r.prixTotal)}</p>
                        </div>

                        <button
                          onClick={() => setSelected(r)}
                          className="mt-3 w-full px-3 py-2 rounded-xl bg-gradient-to-r from-amber-500 to-orange-600 text-white text-xs font-bold hover:from-amber-600 hover:to-orange-700 transition-all"
                        >
                          ⚙️ Gérer · 💳 Paiement
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {selected && (
        <CircuitReservationAdminModal
          reservation={selected}
          onClose={() => setSelected(null)}
          onUpdated={() => {
            fetchReservations();
          }}
        />
      )}
    </div>
  );
};

export default CircuitReservationsManager;
